import { useRef, useEffect, Children, isValidElement, cloneElement } from 'react';
import styled, { css } from 'styled-components';
import { mediaQueries as media } from '@kiwicom/account-global-contexts';

import type { Props as TabsHeaderItemProps } from './TabsHeaderItem';

type Props = {
  readonly activeId?: number;
  readonly setActiveId?: (idx: number) => void;
  readonly children: JSX.Element | JSX.Element[];
};
const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  background-color: ${({ theme }) => theme.orbit.paletteWhite};
  box-shadow: 0 2px 4px -1px rgba(37, 42, 49, 0.12), 0 0 2px -1px rgba(37, 42, 49, 0.16);
  overflow-x: auto;
  scroll-behavior: smooth;
  border-bottom: 1px solid ${({ theme }) => theme.orbit.paletteCloudNormal};
`;
const ChildrenWrapper = styled.div`
  width: 960px;
  display: flex;
  flex-direction: row;
  padding: 0 16px;
  margin: 0 auto;

  ${media.desktop(css`
    padding: 0 ${({ theme }) => theme.orbit.space600};
  `)}
`;
const ContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  flex-shrink: 0;
`;

const TabsHeader = ({ activeId, setActiveId, children }: Props): JSX.Element => {
  const wrapperRef = useRef(null);
  const activeRef = useRef(null);
  useEffect(() => {
    const container = wrapperRef.current;
    const activeTab = activeRef.current;

    // scroll only when the scroll bar is present
    if (container != null && activeTab != null && container.scrollWidth > container.clientWidth) {
      // dividing clientWidth by 2 to scroll to center of the Header item
      container.scrollLeft = activeTab.offsetLeft - activeTab.clientWidth / 2;
    }
  }, [activeId]);

  if ((__DEV__ && activeId == null) || setActiveId == null) {
    console.warn('<TabsHeader> has to be instatiated inside <Tabs> component');
    return null;
  }

  let tabCount = 0;
  Children.forEach(children, (child) => {
    if (isValidElement(child)) {
      // We have some headers only visible in account_beta, they should not be counted
      tabCount++;
    }
  });
  const modifiedChildren = Children.map(children, (child, key) => {
    if (!isValidElement(child)) {
      return null;
    }

    const isActive = activeId === key;
    return cloneElement<Omit<TabsHeaderItemProps, 'children' | 'tabId'>>(child, {
      active: isActive,
      setActiveId,
      idx: key,
      tabCount,
      isLast: Children.count(children) === key,
      ...(isActive
        ? {
            ref: activeRef,
          }
        : null),
    });
  });
  return (
    <Wrapper ref={wrapperRef}>
      <ChildrenWrapper role="tablist">
        <ContentWrapper>{modifiedChildren}</ContentWrapper>
      </ChildrenWrapper>
    </Wrapper>
  );
};

export default TabsHeader;
